import React, { useEffect, useState, useCallback } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

/**
 * Upsell Product Catalog (ER-MOM-06 Phase 0) — FOM defines sellable upsell
 * products (Breakfast / Lunch / Dinner / Activities / Amenities) with a pricing
 * basis, so they can later be offered at check-in and posted to the folio.
 */
const BASIS_LABELS = {
  PER_PAX: 'Per Pax',
  PER_PAX_PER_NIGHT: 'Per Pax / Night',
  PER_NIGHT: 'Per Night',
  PER_STAY: 'Per Stay (flat)',
  PER_UNIT: 'Per Unit / Qty',
};
const CATEGORY_LABELS = {
  BREAKFAST: 'Breakfast', LUNCH: 'Lunch', DINNER: 'Dinner', FNB: 'F&B (other)',
  ACTIVITY: 'Activity', AMENITY: 'Amenity', TRANSPORT: 'Transport', OTHER: 'Other',
};
const emptyForm = { name: '', category: 'BREAKFAST', inclusion_type: '', rate: '', pricing_basis: 'PER_PAX_PER_NIGHT', gst_percent: '', hsn_sac: '', description: '', is_active: true };

function PmsUpsellProducts({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [smsBusy, setSmsBusy] = useState(false);
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) { setRows([]); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/v1/upsell/products?all=1', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to load upsell products'); return; }
      setRows(Array.isArray(data.products) ? data.products : []);
      const cfg = await fetch('/api/v1/upsell/sms-config', { headers: headers() }).then((r) => r.json()).catch(() => ({}));
      setSmsEnabled(cfg.upsell_sms_enabled !== false);
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setLoading(false); }
  }, [selectedProperty, headers]);

  const toggleSmsCampaign = async () => {
    setSmsBusy(true); setError(''); setMessage('');
    try {
      const res = await fetch('/api/v1/upsell/sms-config', { method: 'PUT', headers: headers(true), body: JSON.stringify({ enabled: !smsEnabled }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to update campaign setting'); return; }
      setSmsEnabled(Boolean(data.upsell_sms_enabled));
      setMessage(`Upsell SMS campaign ${data.upsell_sms_enabled ? 'turned ON' : 'turned OFF'} for this property.`);
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setSmsBusy(false); }
  };

  useEffect(() => { load(); }, [load]);

  const isMeal = ['BREAKFAST', 'LUNCH', 'DINNER'].includes(form.category);

  const save = async () => {
    setError(''); setMessage('');
    if (!selectedProperty?.id) return setError('Select a property first');
    if (!String(form.name || '').trim()) return setError('Product name is required');
    try {
      const res = await fetch(editingId ? `/api/v1/upsell/products/${editingId}` : '/api/v1/upsell/products', {
        method: editingId ? 'PUT' : 'POST',
        headers: headers(true),
        body: JSON.stringify({
          name: form.name,
          category: form.category,
          // Auto-map a meal category to its inclusion so we never double-offer
          // (e.g. no breakfast upsell on a CP/MAP/AP booking).
          inclusion_type: isMeal ? form.category : (form.inclusion_type || null),
          rate: form.rate === '' ? 0 : Number(form.rate),
          pricing_basis: form.pricing_basis,
          gst_percent: form.gst_percent === '' ? 0 : Number(form.gst_percent),
          hsn_sac: form.hsn_sac || null,
          description: form.description || null,
          is_active: form.is_active !== false,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Save failed'); return; }
      setMessage(editingId ? 'Upsell product updated' : 'Upsell product created');
      setEditingId(''); setForm(emptyForm);
      load();
    } catch (err) {
      setError(err?.message || 'Network error');
    }
  };

  const editRow = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name || '',
      category: r.category || 'OTHER',
      inclusion_type: r.inclusion_type || '',
      rate: r.rate ?? '',
      pricing_basis: r.pricing_basis || 'PER_STAY',
      gst_percent: r.gst_percent ?? '',
      hsn_sac: r.hsn_sac || '',
      description: r.description || '',
      is_active: r.is_active !== false,
    });
    setError(''); setMessage('');
    scrollToForm();
  };

  const deactivate = async (id, name) => {
    if (!window.confirm(`Deactivate upsell product "${name}"?`)) return;
    setError('');
    try {
      const res = await fetch(`/api/v1/upsell/products/${id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Deactivate failed'); return; }
      load();
    } catch (err) { setError(err?.message || 'Network error'); }
  };

  const inputStyle = { padding: '0.55rem', border: '1px solid #dcdde1', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' };

  // Example price preview for a sample 2 pax / 2 nights stay.
  const examplePrice = (() => {
    const rate = Number(form.rate) || 0;
    const pax = 2, nights = 2;
    switch (form.pricing_basis) {
      case 'PER_PAX': return rate * pax;
      case 'PER_PAX_PER_NIGHT': return rate * pax * nights;
      case 'PER_NIGHT': return rate * nights;
      case 'PER_UNIT': return rate;
      default: return rate;
    }
  })();

  if (!selectedProperty?.id) {
    return <div className="page-shell" style={{ color: '#6b7280' }}>Select a property to manage upsell products.</div>;
  }

  return (
    <div className="page-shell">
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.4rem' }}>Front Office &gt; Upsell Products</div>
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.2rem' }}>Upsell Product Catalog</h2>
      <p style={{ color: '#6b7280', fontSize: '0.88rem', marginBottom: '1rem' }}>
        Define sellable upsell products and how they're priced. These become the one-tap offers at check-in and post to the guest folio.
        Room-type <strong>upgrades</strong> are priced automatically from the rate plan and don't need an entry here.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem', flexWrap: 'wrap',
        background: smsEnabled ? '#f0fdf4' : '#fef2f2', border: `1px solid ${smsEnabled ? '#bbf7d0' : '#fecaca'}`,
        borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontWeight: 700, color: smsEnabled ? '#166534' : '#b91c1c', fontSize: '0.92rem' }}>
            📣 Upsell SMS Campaign — {smsEnabled ? 'ON' : 'OFF'}
          </div>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>
            Automated pre-arrival / in-stay / pre-departure upsell messages to guests. Turning this off does <strong>not</strong> affect the one-tap offers shown at check-in.
          </div>
        </div>
        <button onClick={toggleSmsCampaign} disabled={smsBusy}
          style={{ border: 'none', borderRadius: 6, padding: '0.45rem 1rem', fontWeight: 700, cursor: smsBusy ? 'default' : 'pointer', whiteSpace: 'nowrap',
            background: smsEnabled ? '#dc2626' : '#16a34a', color: '#fff' }}>
          {smsBusy ? '…' : smsEnabled ? 'Turn Campaign OFF' : 'Turn Campaign ON'}
        </button>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: '0.6rem' }}>{error}</div>}
      {message && <div className="success-banner" style={{ marginBottom: '0.6rem' }}>{message}</div>}

      <div className="filter-bar" ref={formRef} style={{ marginBottom: '1rem', ...editHighlightStyle(editHighlight) }}>
        <h3>{editingId ? 'Edit Upsell Product' : 'Add Upsell Product'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.7rem' }}>
          <input placeholder="Product Name (e.g. Buffet Breakfast)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={inputStyle}>
            {Object.entries(CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={form.pricing_basis} onChange={(e) => setForm({ ...form, pricing_basis: e.target.value })} style={inputStyle}>
            {Object.entries(BASIS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input type="number" min="0" step="0.01" placeholder="Rate (₹)" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} style={inputStyle} />
          <input type="number" min="0" step="0.01" placeholder="GST %" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} style={inputStyle} />
          <input placeholder="HSN/SAC (optional)" value={form.hsn_sac} onChange={(e) => setForm({ ...form, hsn_sac: e.target.value })} style={inputStyle} />
          {!isMeal && (
            <select value={form.inclusion_type} onChange={(e) => setForm({ ...form, inclusion_type: e.target.value })} style={inputStyle} title="If this product covers a meal that some rate plans already include, set it so we don't double-offer.">
              <option value="">Covers inclusion… (none)</option>
              <option value="BREAKFAST">Breakfast</option>
              <option value="LUNCH">Lunch</option>
              <option value="DINNER">Dinner</option>
            </select>
          )}
          <input placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={inputStyle} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active
          </label>
        </div>
        <div style={{ marginTop: '0.6rem', fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>
          Example for a 2-pax / 2-night stay: <strong>₹{examplePrice.toLocaleString('en-IN')}</strong> ({BASIS_LABELS[form.pricing_basis]}{form.pricing_basis === 'PER_UNIT' ? ', × qty at sale' : ''})
          {isMeal && <span> · auto-tagged as a <strong>{CATEGORY_LABELS[form.category]}</strong> inclusion (won't be offered if the booking already includes it)</span>}
        </div>
        <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={save} className="btn-primary" style={{ padding: '0.55rem 1.1rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>{editingId ? 'Update' : 'Add'}</button>
          {editingId && <button onClick={() => { setEditingId(''); setForm(emptyForm); }} className="btn-muted" style={{ padding: '0.55rem 1.1rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>}
        </div>
      </div>

      <div className="filter-bar">
        <h3>Products</h3>
        {loading ? <p>Loading…</p> : rows.length === 0 ? (
          <p style={{ color: '#6b7280' }}>No upsell products yet. Add breakfast, dinner, activities, etc. above.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                  <th style={{ padding: '0.55rem', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '0.55rem', textAlign: 'left' }}>Category</th>
                  <th style={{ padding: '0.55rem', textAlign: 'left' }}>Pricing</th>
                  <th style={{ padding: '0.55rem', textAlign: 'right' }}>Rate</th>
                  <th style={{ padding: '0.55rem', textAlign: 'right' }}>GST%</th>
                  <th style={{ padding: '0.55rem', textAlign: 'center' }}>Active</th>
                  <th style={{ padding: '0.55rem', textAlign: 'left' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #dcdde1', opacity: r.is_active ? 1 : 0.55 }}>
                    <td style={{ padding: '0.5rem', fontWeight: 600 }}>
                      {r.name}
                      {r.inclusion_type && <span style={{ marginLeft: 6, fontSize: '0.68rem', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 4, padding: '0 5px' }}>{r.inclusion_type}</span>}
                    </td>
                    <td style={{ padding: '0.5rem' }}>{CATEGORY_LABELS[r.category] || r.category}</td>
                    <td style={{ padding: '0.5rem' }}>{BASIS_LABELS[r.pricing_basis] || r.pricing_basis}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>₹{Number(r.rate).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{Number(r.gst_percent) || 0}%</td>
                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>{r.is_active ? 'Yes' : 'No'}</td>
                    <td style={{ padding: '0.5rem', display: 'flex', gap: '0.4rem' }}>
                      <button onClick={() => editRow(r)} style={{ padding: '0.45rem 0.8rem', border: 'none', borderRadius: '6px', background: '#3498db', color: '#fff', cursor: 'pointer' }}>Edit</button>
                      {r.is_active && <button onClick={() => deactivate(r.id, r.name)} className="btn-danger" style={{ padding: '0.45rem 0.8rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Deactivate</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PmsUpsellProducts;
